"use client";

import {
    useState,
    useMemo,
    useEffect,
    useRef,
} from "react";

import useAllLeagues from "@/hooks/useAllLeagues";
import LeagueFilterV18Controls from "./LeagueFilterV18Controls";
import LeagueFilterV18Dropdown from "./LeagueFilterV18Dropdown";

const LIST_SCROLLBAR = "football-scrollbar";

const FIELD_BASE =
    "box-border h-[38px] w-full min-w-0 rounded-lg border border-solid border-[var(--theme-primary)] bg-[var(--theme-foreground)] px-2 text-sm text-[var(--theme-text)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-primary)]";

export default function LeagueFilterV18({
    selectedIds = [],
    onChange,
    className = "",
    anchorRef: _anchorRef,
}) {
    const { leagues, loading } = useAllLeagues();
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState("");
    const [tempSelectedIds, setTempSelectedIds] = useState(selectedIds || []);
    const [viewMode, setViewMode] = useState("league");
    const rootRef = useRef(null);

    useEffect(() => {
        setTempSelectedIds(selectedIds || []);
    }, [selectedIds]);

    useEffect(() => {
        const onDoc = (e) => {
            if (!open) return;
            const el = rootRef.current;
            if (el && !el.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const normalizedLeagues = useMemo(
        () =>
            (leagues || []).map((l) => ({
                ...l,
                country: l.country || l.countryName || "Khác",
            })),
        [leagues],
    );

    const filteredLeagues = useMemo(() => {
        if (!keyword.trim()) return normalizedLeagues || [];
        const lower = keyword.toLowerCase();
        return (normalizedLeagues || []).filter((league) => {
            const nameVN = (league.nameVN || "").toLowerCase();
            const name = (league.name || "").toLowerCase();
            const country = (league.country || "").toLowerCase();
            return (
                nameVN.includes(lower) ||
                name.includes(lower) ||
                country.includes(lower)
            );
        });
    }, [normalizedLeagues, keyword]);

    const leaguesByCountry = useMemo(() => {
        const groups = {};
        for (const league of filteredLeagues) {
            const countryKey = league.country || "Khác";
            if (!groups[countryKey]) {
                groups[countryKey] = {
                    country: countryKey,
                    countryLogo: league.countryLogo || null,
                    items: [],
                };
            }
            groups[countryKey].items.push(league);
        }
        return Object.values(groups);
    }, [filteredLeagues]);

    const handleToggleLeague = (id) => {
        setTempSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const handleSelectAll = () => {
        const allIds = (filteredLeagues || []).map((l) => l.id);
        setTempSelectedIds(allIds);
    };

    const handleClearAll = () => {
        setTempSelectedIds([]);
    };

    const handleConfirm = () => {
        onChange?.(tempSelectedIds);
        setOpen(false);
    };

    const hasSelection = selectedIds && selectedIds.length > 0;
    const triggerLabel = hasSelection
        ? `${selectedIds.length} giải đấu`
        : "Tất cả giải đấu";

    const allFilteredSelected =
        filteredLeagues.length > 0 &&
        filteredLeagues.every((l) => tempSelectedIds.includes(l.id));

    const liActiveAll = tempSelectedIds.length === 0;

    return (
        <div
            ref={rootRef}
            className={`relative w-full min-w-0 text-[var(--theme-text)] ${className}`}
        >
            <LeagueFilterV18Controls
                keyword={keyword}
                onKeywordChange={setKeyword}
                onOpen={() => setOpen(true)}
                onToggleOpen={() => setOpen((v) => !v)}
                open={open}
                triggerLabel={triggerLabel}
                fieldBaseClassName={FIELD_BASE}
            />

            <LeagueFilterV18Dropdown
                open={open}
                loading={loading}
                filteredLeagues={filteredLeagues}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                liActiveAll={liActiveAll}
                tempSelectedIds={tempSelectedIds}
                leaguesByCountry={leaguesByCountry}
                allFilteredSelected={allFilteredSelected}
                onClearAll={handleClearAll}
                onToggleLeague={handleToggleLeague}
                onSelectAll={handleSelectAll}
                onConfirm={handleConfirm}
                listScrollbarClassName={LIST_SCROLLBAR}
            />
        </div>
    );
}
